import { formatearMoneda } from "@uirtus/shared";
import { useFacturasPendientes } from "../lib/pagos";
import { Aviso, Entrada } from "./ui";

export interface AplicacionFactura {
  facturaId: string;
  impPagado: string;
}

const soloNumeroDecimal = (v: string): string => v.replace(/[^\d.]/g, "");

interface Props {
  clienteId: string | undefined;
  value: AplicacionFactura[];
  onChange: (nuevo: AplicacionFactura[]) => void;
}

/**
 * Lista las facturas PPD con saldo del cliente elegido y deja capturar
 * cuánto se aplica a cada una. El total del pago no se pide aparte: se
 * calcula sumando lo aplicado, así no hay forma de que el monto capturado
 * no cuadre con lo que el backend va a validar.
 */
export function AplicacionPagos({ clienteId, value, onChange }: Props) {
  const { data, isLoading } = useFacturasPendientes(clienteId);
  const facturas = data ?? [];

  const montoDe = (facturaId: string): string =>
    value.find((v) => v.facturaId === facturaId)?.impPagado ?? "";

  const actualizar = (facturaId: string, impPagado: string) => {
    const limpio = soloNumeroDecimal(impPagado);
    const resto = value.filter((v) => v.facturaId !== facturaId);
    onChange(limpio ? [...resto, { facturaId, impPagado: limpio }] : resto);
  };

  const total = value.reduce((acc, v) => acc + (Number(v.impPagado) || 0), 0);

  if (!clienteId) {
    return (
      <p className="rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700">
        Selecciona un cliente para ver sus facturas pendientes.
      </p>
    );
  }

  if (isLoading) return <p className="text-sm text-slate-500">Cargando…</p>;

  if (facturas.length === 0) {
    return (
      <Aviso tono="alerta">
        Este cliente no tiene facturas PPD con saldo pendiente.
      </Aviso>
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500 dark:border-slate-800 dark:bg-slate-800/50">
            <tr>
              <th className="px-3 py-2">Factura</th>
              <th className="px-3 py-2 text-right">Saldo</th>
              <th className="px-3 py-2 text-right">Aplicar</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {facturas.map((f) => (
              <tr key={f.id}>
                <td className="px-3 py-2 font-mono text-xs">
                  {f.serie && f.folio !== null ? `${f.serie}-${f.folio}` : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {formatearMoneda(f.saldoInsoluto, f.moneda)}
                </td>
                <td className="px-3 py-2">
                  <Entrada
                    value={montoDe(f.id)}
                    onChange={(e) => actualizar(f.id, e.target.value)}
                    inputMode="decimal"
                    placeholder="0.00"
                    className="ml-auto w-32 text-right font-mono"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-2 text-sm">
        <span className="text-slate-500">Total del pago</span>
        <span className="font-mono font-semibold">{formatearMoneda(total)}</span>
      </div>
    </div>
  );
}
